import { FormInput, Spinner } from "components";
import { TimedMessage } from "components/TimedMessage";
import { ZFeatureFlag, ZFlags } from "components/ZFeatureFlag";
import { getRowIds } from "helpers/data/dataHandling";
import { checkIfNumber } from "helpers/numbersFormater";
import { useStockFilter } from "hooks";
import { BulkEditManagedStock } from "models/daoModels/managedStock/BulkEditManagedStock";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { cleanBulkEditDetails, updateBulkEditField } from "redux/slices/BulkEdit";
import { getDepartments } from "redux/slices/Departments";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { bulkUpdate, getStockTable, updateStockField } from "redux/slices/Stocks";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getSupplierNameMappings } from "redux/slices/SupplierNameMappings";
import { getSuppliers } from "redux/slices/Suppliers";
import { RootState } from "redux/store";

export function BulkEdit(props: any) {
    const dispatch = useAppDispatch();
    const getStockFilter = useStockFilter();
    const stocks = useAppSelector((state: RootState) => state.stocks);
    const suppliers = useAppSelector((state: RootState) => state.suppliers);
    const supplierNameMappings = useAppSelector((state: RootState) => state.supplierNameMappings);
    const departments = useAppSelector((state: RootState) => state.department);
    const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
    const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
    const bulkEdit = useAppSelector((state: RootState) => state.bulkEdit);
    const [canSave, setCanSave] = useState(false);
    const manufacturers = useAppSelector((state: RootState) => state.managedManufacturers);

    useEffect(() => {
        suppliers.table.length === 0 && dispatch(getSuppliers());
        departments.table.length === 0 && dispatch(getDepartments());
        supplierNameMappings.table.length === 0 && dispatch(getSupplierNameMappings());
        manufacturers.table.length === 0 && dispatch(getManufacturers());
    }, []);

    useEffect(() => {
        if (bulkEdit.department) dispatch(getSubDepartments(bulkEdit.department));
    }, [bulkEdit.department]);

    useEffect(() => {
        dispatch(cleanBulkEditDetails());
    }, [props.show]);

    function handleChange(key: string, value: any) {
        dispatch(updateBulkEditField({ key: key, value: value }));
    }

    function selectedStocksCount() {
        let count = 0;
        if (stocks.selectAllFilter) {
            let deselectedRows = 0;
            stocks.deselectedRows.forEach((row: any) => deselectedRows += row.length);
            count = stocks.totalRows - deselectedRows;
        } else {
            stocks.selectedRows.forEach((row: any) => row.forEach(() => count++));
        }
        return count;
    }

    useEffect(() => {
        if (bulkEdit.locked !== 0 || bulkEdit.ethical !== 0 || bulkEdit.active !== 0 || bulkEdit.gst !== "" || bulkEdit.minStock !== 0 || bulkEdit.maxStock !== 0 || bulkEdit.minOrderQty !== 0 || bulkEdit.cartonSize !== 0 || bulkEdit.stockGroup !== 0 || bulkEdit.subDepartment !== 0 || bulkEdit.department !== 0 || bulkEdit.supplier !== 0) {
            setCanSave(true);
        } else {
            setCanSave(false);
        }
    }, [bulkEdit]);

    function handleBulkUpdate() {
        handleChange("loading", true);
        const bulkUpdateParams: BulkEditManagedStock = {
            filter: {
                ...getStockFilter(),
                ids: (stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows)),
			    excludedIds: (stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : []),
            },
            supplier: (bulkEdit.supplier ? { isChanging: true, newValue: (bulkEdit.supplier === 0 ? null : bulkEdit.supplier), oldValue: null } : undefined),
            department: (bulkEdit.department ? { isChanging: true, newValue: (bulkEdit.department === 0 ? null : bulkEdit.department), oldValue: null } : undefined),
            subDepartment: (bulkEdit.subDepartment ? { isChanging: true, newValue: (bulkEdit.subDepartment === 0 ? null : bulkEdit.subDepartment), oldValue: null } : undefined),
            stockGroup: (bulkEdit.stockGroup ? { isChanging: true, newValue: (bulkEdit.stockGroup === 0 ? null : bulkEdit.stockGroup), oldValue: null } : undefined),
            manufacturer: (bulkEdit.manufacturer ? { isChanging: true, newValue: (bulkEdit.manufacturer === 0 ? null : bulkEdit.manufacturer), oldValue: null } : undefined),
            minOrderQty: (bulkEdit.minOrderQty ? { isChanging: true, newValue: bulkEdit.minOrderQty, oldValue: 0 } : undefined),
            cartonSize: (bulkEdit.cartonSize ? { isChanging: true, newValue: bulkEdit.cartonSize, oldValue: 0 } : undefined),
            minStockLevel: (bulkEdit.minStock ? { isChanging: true, newValue: bulkEdit.minStock, oldValue: 0 } : undefined),
            maxStockLevel: (bulkEdit.maxStock ? { isChanging: true, newValue: bulkEdit.maxStock, oldValue: 0 } : undefined),
            gstCode: (bulkEdit.gst ? { isChanging: true, newValue: bulkEdit.gst, oldValue: "Y" } : undefined),
            active: (bulkEdit.active ? { isChanging: true, newValue: bulkEdit.active === 1, oldValue: false } : undefined),
            ethical: (bulkEdit.ethical ? { isChanging: true, newValue: bulkEdit.ethical === 1, oldValue: false } : undefined),
            locked: (bulkEdit.locked ? { isChanging: true, newValue: bulkEdit.locked === 1, oldValue: false } : undefined),
        };
        dispatch(bulkUpdate(bulkUpdateParams)).then((res) => {
            handleChange("loading", false);
            if (res.data) {
                dispatch(updateStockField({ key: "selectAllFilter", value: false }));
                dispatch(updateStockField({ key: "selectedRows", value: [] }));
                dispatch(updateStockField({ key: "deselectedRows", value: [] }));
                dispatch(updateStockField({ key: "pageIndex", value: 0 }));
                dispatch(getStockTable({
                    ...getStockFilter(),
                }));
                props.handleClose();
            }
        });
    }

    return (
        <Modal backdrop={bulkEdit.loading ? "static" : true} size="lg" centered show={props.show} onHide={props.handleClose}>
            <Modal.Header closeButton={!bulkEdit.loading}>
                <Modal.Title>Bulk Edit - {selectedStocksCount()} Stocks Selected</Modal.Title>
            </Modal.Header>
            <Modal.Body className={`${bulkEdit.loading && "disabled-div"}`}>
                <Row>
                    <Col xl={6}>
                        <Row className="mt-2 mb-2 ">
                            <h6 className="form-title">Stocks Details</h6>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Preferred Supplier</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.supplier}
                                        onChange={(e) => handleChange("supplier", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        {suppliers.table.map((supplier: any, index: any) => {
                                            return <option key={index} value={supplier.id}>{supplier.name}</option>
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Department</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.department}
                                        onChange={(e) => {
                                            handleChange("department", parseInt(e.target.value));
                                            if (parseInt(e.target.value) === 0) handleChange("subDepartment", 0);
                                        }}
                                    >
                                        <option value="0"> </option>
                                        {departments.table.map((department: any, index: any) => {
                                            return <option key={index} value={department.id}>{department.name}</option>
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Sub Department</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.subDepartment}
                                        onChange={(e) => handleChange("subDepartment", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        {bulkEdit.department !== 0 && <>
                                            {subDepartments.table.map((subDepartment: any, index: any) => {
                                                return <option key={index} value={subDepartment.id}>{subDepartment.name}</option>
                                            })}
                                        </>}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Stock Group</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.stockGroup}
                                        onChange={(e) => handleChange("stockGroup", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        {stockGroups.table.map((stockGroup: any, index: any) => {
                                            return <option key={index} value={stockGroup.id}>{stockGroup.name}</option>
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Manufacturers</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.manufacturer}
                                        onChange={(e) => handleChange("manufacturer", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        {manufacturers.table.map((manufacturer: any, index: any) => {
                                            return <option key={index} value={manufacturer.id}>{manufacturer.name}</option>
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Min Order Qty</Form.Label></Col>
                            <Col xs={7}>
                                <FormInput
                                    min={0}
                                    required
                                    type="number"
                                    name="minOrderQty"
                                    onKeyDown={(e) => checkIfNumber(e)}
                                    value={bulkEdit.minOrderQty ? bulkEdit.minOrderQty : ""}
                                    onChange={(e) => handleChange("minOrderQty", parseInt(e.target.value))}
                                    onBlur={(e) => { if (parseInt(e.target.value) < 0) handleChange("minOrderQty", 0) }}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Carton Size</Form.Label></Col>
                            <Col xs={7}>
                                <FormInput
                                    min={0}
                                    required
                                    type="number"
                                    name="cartonSize"
                                    onKeyDown={(e) => checkIfNumber(e)}
                                    value={bulkEdit.cartonSize ? bulkEdit.cartonSize : ""}
                                    onChange={(e) => handleChange("cartonSize", parseInt(e.target.value))}
                                    onBlur={(e) => { if (parseInt(e.target.value) < 0) handleChange("cartonSize", 0) }}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Min Stock</Form.Label></Col>
                            <Col xs={7}>
                                <FormInput
                                    min={0}
                                    required
                                    type="number"
                                    name="minStock"
                                    onKeyDown={(e) => checkIfNumber(e)}
                                    value={bulkEdit.minStock ? bulkEdit.minStock : ""}
                                    onChange={(e) => handleChange("minStock", parseInt(e.target.value))}
                                    onBlur={(e) => { if (parseInt(e.target.value) < 0) handleChange("minStock", 0) }}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Max Stock</Form.Label></Col>
                            <Col xs={7}>
                                <FormInput
                                    min={0}
                                    required
                                    type="number"
                                    name="maxStock"
                                    onKeyDown={(e) => checkIfNumber(e)}
                                    value={bulkEdit.maxStock ? bulkEdit.maxStock : ""}
                                    onChange={(e) => handleChange("maxStock", parseInt(e.target.value))}
                                    onBlur={(e) => { if (parseInt(e.target.value) < 0) handleChange("maxStock", 0) }}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xl={6}>
                        <Row className="mt-2 mb-2">
                            <h6 className="form-title">Pricing Details</h6>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>GST</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.gst}
                                        onChange={(e) => e.target.value === "0" ? handleChange("gst", "") : handleChange("gst", e.target.value)}>
                                        <option value="0"> </option>
                                        <option value="F">F - 0% Buy, 0% Sell</option>
                                        <option value="N">N - 10% Buy, 0% Sell</option>
                                        <option value="Y">Y - 10% Buy, 10% Sell</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Active</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.active}
                                        onChange={(e) => handleChange("active", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        <option value="1">Yes</option>
                                        <option value="2">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2 mb-2">
                            <Col xs={5}><Form.Label>Ethical</Form.Label></Col>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Select className="custom-select"
                                        value={bulkEdit.ethical}
                                        onChange={(e) => handleChange("ethical", parseInt(e.target.value))}
                                    >
                                        <option value="0"> </option>
                                        <option value="1">Yes</option>
                                        <option value="2">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <ZFeatureFlag flag="ZFreedom.Stores.LockDownFields" value={true}>
                            <Row className="mt-2 mb-2">
                                <Col xs={5}><Form.Label>Locked</Form.Label></Col>
                                <Col xs={7}>
                                    <Form.Group>
                                        <Form.Select className="custom-select"
                                            value={bulkEdit.locked}
                                            onChange={(e) => handleChange("locked", parseInt(e.target.value))}
                                        >
                                            <option value="0"> </option>
                                            <option value="1">Yes</option>
                                            <option value="2">No</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </ZFeatureFlag>
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col sm={3}></Col>
                    <Col sm={9} className="d-flex flex-row-reverse">
                        {canSave &&
                            <Button style={{alignItems: "center"}}  variant="primary" className="btn ms-1 d-flex cta-button" disabled={bulkEdit.loading} onClick={handleBulkUpdate}>
                                {bulkEdit.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
                                {bulkEdit.loading ? <>Saving...</> : <>Save</>}
                            </Button>
                        }

                        <Button variant="secondary" className="btn ms-1 cta-button" disabled={bulkEdit.loading} onClick={() => props.handleClose()}>Cancel</Button>
                        <TimedMessage message={"Please wait while we edit all your data..."} enabled={bulkEdit.loading} delay={30} className="mb-0 py-1 px-2" />
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
}